import type { AbilityGrade, GameState, TeamSelection } from './types'

const GRADE_VALUE: Record<AbilityGrade, number> = {
  Limited: 40,
  'Below Standard': 50,
  'League Standard': 60,
  'Above Standard': 70,
  Strong: 80,
  Elite: 90,
  Exceptional: 96,
}

const selectedIds = (team: TeamSelection) => Object.values(team)
  .flat()
  .filter(entry => entry.position !== 'EMG')
  .map(entry => entry.playerId)

const playerAbility = (state: GameState, playerId: string) => {
  const player = state.club.players.find(candidate => candidate.id === playerId)
  if (!player) return 50
  const grades = Object.values(player.abilities) as AbilityGrade[]
  return grades.reduce((sum, grade) => sum + GRADE_VALUE[grade], 0) / grades.length
}

const average = (values: number[]) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0

export interface SelectionInfluence {
  selectionChanges: number
  rawSelectionEdge: number
  readinessMargin: number
  explanation: string
}

export const calculateSelectionInfluence = (state: GameState): SelectionInfluence => {
  const currentIds = selectedIds(state.staffPlan.team)
  const baselineIds = selectedIds(state.week?.staffDefaults?.team ?? state.staffPlan.team)
  const baselineSet = new Set(baselineIds)
  const selectionChanges = currentIds.filter(id => !baselineSet.has(id)).length
  const currentAbility = average(currentIds.map(id => playerAbility(state, id)))
  const baselineAbility = average(baselineIds.map(id => playerAbility(state, id)))
  const selectedPlayers = currentIds.map(id => state.club.players.find(player => player.id === id)).filter((player): player is GameState['club']['players'][number] => Boolean(player))
  const readinessMargin = average(selectedPlayers.map(player =>
    ((player.form - 50) * 0.04)
    + ((player.fitness - 90) * 0.04)
    - ((player.fatigue - 10) * 0.05)
    + ((player.morale - 50) * 0.03)))
  const changedPlayers = currentIds
    .filter(id => !baselineSet.has(id))
    .map(id => state.club.players.find(player => player.id === id))
    .filter((player): player is GameState['club']['players'][number] => Boolean(player))
  const opponentText = [...state.opponent.strengths, ...state.opponent.weaknesses, state.opponent.style].join(' ').toLowerCase()
  const matchupEdge = changedPlayers.reduce((edge, player) => {
    const role = player.primaryRole.toLowerCase()
    const paceFit = opponentText.includes('pace') || opponentText.includes('speed') || opponentText.includes('transition')
    const contestFit = opponentText.includes('contest') || opponentText.includes('clearance') || opponentText.includes('ground ball')
    const aerialFit = opponentText.includes('aerial') || opponentText.includes('marking')
    const roleFit = (paceFit && (role.includes('wing') || role.includes('midfield')))
      || (contestFit && (role.includes('midfield') || role.includes('ruck')))
      || (aerialFit && (role.includes('forward') || role.includes('defender')))
    const readiness = ((player.form - 50) + (player.fitness - 90) - player.fatigue) / 20
    return edge + readiness + (roleFit ? 1.5 : 0)
  }, 0)
  const rawSelectionEdge = ((currentAbility - baselineAbility) * 0.65) + matchupEdge
  const explanation = selectionChanges === 0
    ? readinessMargin < -2 ? 'The selected side carried material readiness risk.' : 'The staff-selected side retained its expected balance.'
    : rawSelectionEdge >= 0
      ? `${selectionChanges} Coach selection change${selectionChanges === 1 ? '' : 's'} improved matchup potential while adding execution complexity.`
      : `${selectionChanges} Coach selection change${selectionChanges === 1 ? '' : 's'} reduced expected reliability for this matchup.`

  return { selectionChanges, rawSelectionEdge, readinessMargin, explanation }
}
